/**
 * Inventory / work-order location_code for any warehouse, not only MRP planning sites.
 *
 * MRP planning codes (`sgs`, `suntronic`, `kodiak`) stay on `public.location.mrp_site_code`.
 * Other enabled Megaventory warehouses use `abbreviation` (same as `mrp.inventory_snapshot.location_code`).
 */

export interface OperationalLocationCodeFields {
  publicLocationId?: number | null;
  mrpSiteCode?: string | null;
  abbreviation?: string | null;
}

export function operationalLocationCode(fields: OperationalLocationCodeFields): string {
  const site = fields.mrpSiteCode?.trim();
  if (site) return site;
  const abbreviation = (fields.abbreviation ?? "").trim();
  if (abbreviation.length > 0) return abbreviation;
  if (
    fields.publicLocationId != null &&
    Number.isFinite(fields.publicLocationId) &&
    fields.publicLocationId > 0
  ) {
    return String(fields.publicLocationId);
  }
  return "";
}

/** SQL expression against `public.location` — matches {@link operationalLocationCode}. */
export const SQL_OPERATIONAL_LOCATION_CODE = `coalesce(
  nullif(trim(public.location.mrp_site_code), ''),
  nullif(trim(public.location.abbreviation), ''),
  public.location.id::text
)`;

/** Lowercased operational code for filter comparisons. */
export function normalizedOperationalLocationCode(fields: OperationalLocationCodeFields): string {
  return operationalLocationCode(fields).trim().toLowerCase();
}

/**
 * Scalar subquery: operational location code for a `public.location.id` column
 * (purchase order ship-to, transfer origin/destination). Empty when the id is null.
 */
export function sqlOperationalLocationCodeForPublicLocationId(columnSql: string): string {
  return `lower(coalesce((
    select ${SQL_OPERATIONAL_LOCATION_CODE}
      from public.location
     where public.location.id = ${columnSql}
  ), ''))`;
}
